import { getMerchantClient } from '@beatq/sdk-client';
import {
  mapErrorCode,
  readDataFromEnvelope,
  readEntityFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
} from '@beatq/shared';
import { WEEKDAYS } from './catalog.js';
import {
  MAX_OPENING_PERIODS,
  periodsFromDayDraft,
  type HoursDayDraft,
  type HoursDraft,
} from './hoursTemplates.js';

type ApiErr = { error?: { code: string; message: string } };

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

/** BE dayOfWeek: 0=Sunday … 6=Saturday (JS Date.getDay). */
export const WEEKDAY_TO_DOW: Record<(typeof WEEKDAYS)[number], number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

export const DOW_TO_WEEKDAY: Record<number, (typeof WEEKDAYS)[number]> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

export type MerchantBusiness = {
  id?: string;
  legalName?: string;
  tradingName?: string;
  billingEmail?: string;
  description?: string | null;
  status?: string;
};

export type BranchSummary = {
  id?: string;
  name?: string;
  publicReference?: string | null;
  status?: string;
  tradingState?: string | null;
  postcode?: string | null;
  liveOrderCount?: number | null;
  ordersPausedUntil?: string | null;
  ordersPausedReason?: string | null;
  snoozedUntil?: string | null;
};

function normalizeBranchMatchName(value: string | undefined): string {
  return value?.trim().toLocaleLowerCase() ?? '';
}

function normalizeBranchMatchPostcode(value: string | null | undefined): string {
  return value?.replace(/\s+/g, '').toUpperCase() ?? '';
}

/**
 * A create request can succeed before pickup-location verification fails.
 * Match that durable branch so a retry resumes verification instead of
 * submitting a second create request.
 */
export function findMatchingCreatedBranch(
  branches: BranchSummary[],
  input: { name: string; postcode: string },
): BranchSummary | undefined {
  const name = normalizeBranchMatchName(input.name);
  const postcode = normalizeBranchMatchPostcode(input.postcode);
  return branches.find(
    (branch) =>
      Boolean(branch.id) &&
      normalizeBranchMatchName(branch.name) === name &&
      normalizeBranchMatchPostcode(branch.postcode) === postcode,
  );
}

export type BranchCapacity = {
  maxConcurrentOrders?: number | null;
  maxOrdersPerSlot?: number | null;
  throttleMinutes?: number | null;
};

export type BranchDetail = BranchSummary & {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  region?: string | null;
  county?: string | null;
  countryCode?: string | null;
  contactPhoneE164?: string | null;
  /** @deprecated alias of contactPhoneE164 for display */
  phone?: string | null;
  email?: string | null;
  verticals?: string[];
  description?: string | null;
  supportsDelivery?: boolean;
  supportsCollection?: boolean;
  defaultPrepMinutes?: number | null;
  /** @deprecated aliases mapped from supports* / defaultPrepMinutes */
  acceptsDelivery?: boolean;
  acceptsCollection?: boolean;
  prepTimeMinutes?: number | null;
  ordersPausedUntil?: string | null;
  ordersPausedReason?: string | null;
  /** @deprecated alias of ordersPausedUntil */
  snoozedUntil?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  locationValidationState?: 'UNCONFIRMED' | 'PENDING_CONFIRMATION' | 'VALIDATED' | string;
  locationValidationSource?: string | null;
  locationValidatedAt?: string | null;
  locationVersion?: number | null;
  pickupInstructions?: string | null;
  providerStoreReference?: string | null;
  settings?: {
    capacity?: BranchCapacity;
    deliveryBands?: unknown[];
    substitutionPolicy?: string;
    [key: string]: unknown;
  } | null;
};

export type OpeningHoursDay = {
  day?: string;
  dayOfWeek?: number;
  open?: string | null;
  close?: string | null;
  opensAt?: string | null;
  closesAt?: string | null;
  closed?: boolean;
  fulfilmentMethod?: string;
};

export type SpecialClosure = {
  id?: string;
  overrideId?: string;
  label?: string | null;
  note?: string | null;
  date?: string | null;
  onDate?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  closed?: boolean;
  isClosed?: boolean;
};

export type ServiceArea = {
  areaType?: string | null;
  radiusMetres?: number | null;
  polygon?: ServiceAreaPolygon | null;
  postcodeRules?: Array<{ prefix?: string; isExclusion?: boolean }>;
  description?: string | null;
  isActive?: boolean | null;
};

export type ServiceAreaPolygon = {
  vertices?: Array<{ latitude?: number; longitude?: number; lat?: number; lng?: number }>;
};

export type ServiceAreaPreview = {
  inArea?: boolean;
  latitude?: number;
  longitude?: number;
};

export function serviceAreaPreviewFromEnvelope(envelope: unknown): ServiceAreaPreview | undefined {
  return (
    readEntityFromEnvelope<ServiceAreaPreview>(envelope) ??
    readDataFromEnvelope<ServiceAreaPreview>(envelope)
  );
}

/** Runtime acceptance probe — not fulfilment settings. */
export type AcceptanceProbe = {
  accepting?: boolean;
  reasons?: string[];
};

export type CapacitySettings = BranchCapacity;

export function businessFromEnvelope(envelope: unknown): MerchantBusiness | undefined {
  return (
    readObjectFieldFromEnvelope<MerchantBusiness>(envelope, ['business']) ??
    readDataFromEnvelope<MerchantBusiness>(envelope)
  );
}

export function branchesFromEnvelope(envelope: unknown): BranchSummary[] {
  return readListFromEnvelope<Record<string, unknown>>(envelope, ['branches']).map((raw) => {
    const ordersPausedUntil =
      typeof raw.ordersPausedUntil === 'string' ? raw.ordersPausedUntil : null;
    return {
      id: typeof raw.id === 'string' ? raw.id : undefined,
      name: typeof raw.name === 'string' ? raw.name : undefined,
      publicReference: typeof raw.publicReference === 'string' ? raw.publicReference : null,
      status: typeof raw.status === 'string' ? raw.status : undefined,
      tradingState:
        typeof raw.tradingState === 'string'
          ? raw.tradingState
          : typeof raw.status === 'string'
            ? raw.status
            : null,
      postcode: typeof raw.postcode === 'string' ? raw.postcode : null,
      liveOrderCount: typeof raw.liveOrderCount === 'number' ? raw.liveOrderCount : null,
      ordersPausedUntil,
      ordersPausedReason:
        typeof raw.ordersPausedReason === 'string' ? raw.ordersPausedReason : null,
      snoozedUntil: ordersPausedUntil,
    };
  });
}

function normalizeBranch(raw: Record<string, unknown>): BranchDetail {
  const settings =
    raw.settings && typeof raw.settings === 'object'
      ? (raw.settings as BranchDetail['settings'])
      : null;
  const supportsDelivery =
    typeof raw.supportsDelivery === 'boolean' ? raw.supportsDelivery : undefined;
  const supportsCollection =
    typeof raw.supportsCollection === 'boolean' ? raw.supportsCollection : undefined;
  const defaultPrepMinutes =
    typeof raw.defaultPrepMinutes === 'number' ? raw.defaultPrepMinutes : null;
  const ordersPausedUntil =
    typeof raw.ordersPausedUntil === 'string' ? raw.ordersPausedUntil : null;
  const contact =
    raw.contact && typeof raw.contact === 'object'
      ? (raw.contact as Record<string, unknown>)
      : null;
  const nestedAddress =
    raw.address && typeof raw.address === 'object'
      ? (raw.address as Record<string, unknown>)
      : null;
  const contactPhoneE164 =
    typeof raw.contactPhoneE164 === 'string'
      ? raw.contactPhoneE164
      : typeof raw.phone === 'string'
        ? raw.phone
        : typeof contact?.phone === 'string'
          ? contact.phone
          : null;
  const email =
    typeof raw.email === 'string'
      ? raw.email
      : typeof contact?.email === 'string'
        ? contact.email
        : typeof settings?.contactEmail === 'string'
          ? settings.contactEmail
          : null;
  const verticals = Array.isArray(raw.verticals)
    ? raw.verticals.filter((v): v is string => typeof v === 'string')
    : Array.isArray(settings?.verticals)
      ? (settings?.verticals as unknown[]).filter((v): v is string => typeof v === 'string')
      : [];
  const description =
    typeof raw.description === 'string'
      ? raw.description
      : typeof settings?.description === 'string'
        ? settings.description
        : null;
  const addressLine1 =
    typeof raw.addressLine1 === 'string'
      ? raw.addressLine1
      : typeof nestedAddress?.line1 === 'string'
        ? nestedAddress.line1
        : null;
  const addressLine2 =
    typeof raw.addressLine2 === 'string'
      ? raw.addressLine2
      : typeof nestedAddress?.line2 === 'string'
        ? nestedAddress.line2
        : null;
  const city =
    typeof raw.city === 'string'
      ? raw.city
      : typeof nestedAddress?.city === 'string'
        ? nestedAddress.city
        : null;
  const region =
    typeof raw.region === 'string'
      ? raw.region
      : typeof nestedAddress?.region === 'string'
        ? nestedAddress.region
        : null;
  const county =
    typeof raw.county === 'string'
      ? raw.county
      : typeof nestedAddress?.county === 'string'
        ? nestedAddress.county
        : null;
  const countryCode =
    typeof raw.countryCode === 'string'
      ? raw.countryCode
      : typeof nestedAddress?.countryCode === 'string'
        ? nestedAddress.countryCode
        : null;
  const postcode =
    typeof raw.postcode === 'string'
      ? raw.postcode
      : typeof nestedAddress?.postcode === 'string'
        ? nestedAddress.postcode
        : null;
  const liveOrderCount = typeof raw.liveOrderCount === 'number' ? raw.liveOrderCount : null;

  return {
    id: typeof raw.id === 'string' ? raw.id : undefined,
    name: typeof raw.name === 'string' ? raw.name : undefined,
    publicReference: typeof raw.publicReference === 'string' ? raw.publicReference : null,
    status: typeof raw.status === 'string' ? raw.status : undefined,
    postcode,
    addressLine1,
    addressLine2,
    city,
    region,
    county,
    countryCode,
    contactPhoneE164,
    phone: contactPhoneE164,
    email,
    verticals,
    description,
    liveOrderCount,
    supportsDelivery,
    supportsCollection,
    defaultPrepMinutes,
    acceptsDelivery: supportsDelivery,
    acceptsCollection: supportsCollection,
    prepTimeMinutes: defaultPrepMinutes,
    ordersPausedUntil,
    ordersPausedReason: typeof raw.ordersPausedReason === 'string' ? raw.ordersPausedReason : null,
    snoozedUntil: ordersPausedUntil,
    tradingState: ordersPausedUntil ? 'PAUSED' : typeof raw.status === 'string' ? raw.status : null,
    latitude: (raw.latitude as number | string | null | undefined) ?? null,
    longitude: (raw.longitude as number | string | null | undefined) ?? null,
    locationValidationState:
      typeof raw.locationValidationState === 'string' ? raw.locationValidationState : undefined,
    locationValidationSource:
      typeof raw.locationValidationSource === 'string' ? raw.locationValidationSource : null,
    locationValidatedAt:
      typeof raw.locationValidatedAt === 'string' ? raw.locationValidatedAt : null,
    locationVersion: typeof raw.locationVersion === 'number' ? raw.locationVersion : null,
    pickupInstructions: typeof raw.pickupInstructions === 'string' ? raw.pickupInstructions : null,
    providerStoreReference:
      typeof raw.providerStoreReference === 'string' ? raw.providerStoreReference : null,
    settings,
  };
}

export function isValidE164(value: string | null | undefined): boolean {
  return /^\+[1-9]\d{7,14}$/.test(value?.trim() ?? '');
}

export function branchHasConfirmedPickup(
  branch: Pick<BranchDetail, 'latitude' | 'longitude' | 'locationValidationState'> | undefined,
): boolean {
  return (
    branch?.locationValidationState === 'VALIDATED' &&
    Number.isFinite(Number(branch.latitude)) &&
    Number.isFinite(Number(branch.longitude))
  );
}

export function branchDeliveryReady(
  branch:
    | Pick<
        BranchDetail,
        'contactPhoneE164' | 'phone' | 'latitude' | 'longitude' | 'locationValidationState'
      >
    | undefined,
): boolean {
  return isValidE164(branch?.contactPhoneE164 ?? branch?.phone) && branchHasConfirmedPickup(branch);
}

export function branchFromEnvelope(envelope: unknown): BranchDetail | undefined {
  const nested = readObjectFieldFromEnvelope<Record<string, unknown>>(envelope, ['branch']);
  if (nested) return normalizeBranch(nested);
  const data = readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (data && typeof data === 'object' && 'id' in data && !('branch' in data)) {
    return normalizeBranch(data);
  }
  return undefined;
}

function envelopeRecord(envelope: unknown): Record<string, unknown> | null {
  if (envelope == null || typeof envelope !== 'object') return null;
  const rec = envelope as Record<string, unknown>;
  if (rec.data && typeof rec.data === 'object' && !Array.isArray(rec.data)) {
    return rec.data as Record<string, unknown>;
  }
  return rec;
}

function hoursFromDaysContract(envelope: unknown): OpeningHoursDay[] {
  const nested = envelopeRecord(envelope);
  const days = Array.isArray(nested?.days) ? nested.days : [];
  const out: OpeningHoursDay[] = [];
  for (const raw of days) {
    if (!raw || typeof raw !== 'object') continue;
    const dayRow = raw as Record<string, unknown>;
    const dayName = typeof dayRow.day === 'string' ? dayRow.day.toUpperCase() : undefined;
    const day = WEEKDAYS.includes(dayName as (typeof WEEKDAYS)[number])
      ? (dayName as (typeof WEEKDAYS)[number])
      : undefined;
    if (dayRow.isClosed === true) continue;
    const periods = Array.isArray(dayRow.periods) ? dayRow.periods : [];
    for (const period of periods) {
      if (!period || typeof period !== 'object') continue;
      const p = period as Record<string, unknown>;
      const opensAt = typeof p.opensAt === 'string' ? p.opensAt : null;
      const closesAt = typeof p.closesAt === 'string' ? p.closesAt : null;
      out.push({
        day,
        dayOfWeek: day ? WEEKDAY_TO_DOW[day] : undefined,
        open: opensAt,
        close: closesAt,
        opensAt,
        closesAt,
        closed: false,
      });
    }
  }
  return out;
}

export function openingHoursFromEnvelope(envelope: unknown): OpeningHoursDay[] {
  const hours = readListFromEnvelope<Record<string, unknown>>(envelope, ['hours', 'openingHours']);
  if (hours.length === 0) return hoursFromDaysContract(envelope);
  return hours.map((h) => {
    const dayOfWeek = typeof h.dayOfWeek === 'number' ? h.dayOfWeek : undefined;
    const dayName = typeof h.day === 'string' ? h.day.toUpperCase() : undefined;
    const day =
      dayOfWeek != null
        ? DOW_TO_WEEKDAY[dayOfWeek]
        : WEEKDAYS.includes(dayName as (typeof WEEKDAYS)[number])
          ? (dayName as (typeof WEEKDAYS)[number])
          : undefined;
    const opensAt =
      typeof h.opensAt === 'string' ? h.opensAt : typeof h.open === 'string' ? h.open : null;
    const closesAt =
      typeof h.closesAt === 'string' ? h.closesAt : typeof h.close === 'string' ? h.close : null;
    return {
      day,
      dayOfWeek,
      open: opensAt,
      close: closesAt,
      opensAt,
      closesAt,
      closed: false,
      fulfilmentMethod: typeof h.fulfilmentMethod === 'string' ? h.fulfilmentMethod : undefined,
    };
  });
}

/** Prefer DELIVERY slots for the weekly editor; fall back to COLLECTION / any. */
export function openingHoursDraftFromEnvelope(envelope: unknown): HoursDraft {
  const hours = openingHoursFromEnvelope(envelope);
  const delivery = hours.filter((h) => h.fulfilmentMethod === 'DELIVERY');
  const source = delivery.length > 0 ? delivery : hours;
  const grouped: Partial<
    Record<(typeof WEEKDAYS)[number], Array<{ open: string; close: string }>>
  > = {};
  for (const slot of source) {
    if (!slot.day || !WEEKDAYS.includes(slot.day as (typeof WEEKDAYS)[number])) continue;
    const day = slot.day as (typeof WEEKDAYS)[number];
    const list = grouped[day] ?? [];
    list.push({
      open: slot.open ?? slot.opensAt ?? '09:00',
      close: slot.close ?? slot.closesAt ?? '22:00',
    });
    grouped[day] = list.slice(0, MAX_OPENING_PERIODS);
  }
  const draft: HoursDraft = {};
  for (const day of WEEKDAYS) {
    const periods = grouped[day];
    if (!periods?.length) {
      draft[day] = { open: '09:00', close: '22:00', closed: true, periods: [] };
      continue;
    }
    draft[day] = {
      open: periods[0].open,
      close: periods[0].close,
      closed: false,
      periods,
    };
  }
  return draft;
}

export function lastOrderOffsetFromEnvelope(envelope: unknown): number {
  if (envelope == null || typeof envelope !== 'object') return 0;
  const rec = envelope as Record<string, unknown>;
  const nested =
    rec.data && typeof rec.data === 'object' && !Array.isArray(rec.data)
      ? (rec.data as Record<string, unknown>)
      : null;
  const raw =
    rec.lastOrderOffsetMinutes ??
    rec.lastOrderOffset ??
    nested?.lastOrderOffsetMinutes ??
    nested?.lastOrderOffset;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.min(120, Math.max(0, Math.floor(raw)));
  }
  return 0;
}

export function specialClosuresFromEnvelope(envelope: unknown): SpecialClosure[] {
  const rows = readListFromEnvelope<Record<string, unknown>>(envelope, [
    'specialHours',
    'closures',
  ]);
  return rows.map((r) => {
    const onDate = typeof r.onDate === 'string' ? r.onDate : null;
    const note = typeof r.note === 'string' ? r.note : null;
    const isClosed = Boolean(r.isClosed);
    const id = typeof r.id === 'string' ? r.id : undefined;
    return {
      id,
      overrideId: id,
      label: note ?? (isClosed ? 'Closed' : 'Special hours'),
      note,
      date: onDate,
      onDate,
      startsAt: onDate,
      endsAt: onDate,
      closed: isClosed,
      isClosed,
    };
  });
}

export function serviceAreaFromEnvelope(envelope: unknown): ServiceArea | undefined {
  const area =
    readObjectFieldFromEnvelope<ServiceArea>(envelope, ['serviceArea']) ??
    readEntityFromEnvelope<ServiceArea>(envelope, ['serviceArea']);
  if (!area) return undefined;
  const extra = area as ServiceArea & { postcodes?: unknown; postcodeRules?: unknown };
  return {
    ...area,
    postcodeRules: Array.isArray(extra.postcodeRules)
      ? extra.postcodeRules
      : Array.isArray(extra.postcodes)
        ? (extra.postcodes as ServiceArea['postcodeRules'])
        : extra.postcodeRules,
  };
}

export function acceptanceFromEnvelope(envelope: unknown): AcceptanceProbe | undefined {
  const data = readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (!data || typeof data !== 'object') return undefined;
  if (!('accepting' in data) && !('reasons' in data)) return undefined;
  return {
    accepting: Boolean(data.accepting),
    reasons: Array.isArray(data.reasons) ? (data.reasons as string[]) : [],
  };
}

export function capacityFromBranch(branch: BranchDetail | undefined): CapacitySettings | undefined {
  const cap = branch?.settings?.capacity;
  if (!cap || typeof cap !== 'object') return undefined;
  return {
    maxConcurrentOrders:
      typeof cap.maxConcurrentOrders === 'number' ? cap.maxConcurrentOrders : null,
    maxOrdersPerSlot: typeof cap.maxOrdersPerSlot === 'number' ? cap.maxOrdersPerSlot : null,
    throttleMinutes: typeof cap.throttleMinutes === 'number' ? cap.throttleMinutes : null,
  };
}

/** @deprecated ops route — do not use for capacity settings form */
export function capacityFromEnvelope(envelope: unknown): CapacitySettings | undefined {
  const nested = readObjectFieldFromEnvelope<Record<string, unknown>>(envelope, ['capacity']);
  const data = nested ?? readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (!data || typeof data !== 'object') return undefined;
  return {
    maxConcurrentOrders:
      typeof data.maxConcurrentOrders === 'number' ? data.maxConcurrentOrders : null,
    maxOrdersPerSlot: typeof data.maxOrdersPerSlot === 'number' ? data.maxOrdersPerSlot : null,
    throttleMinutes: typeof data.throttleMinutes === 'number' ? data.throttleMinutes : null,
  };
}

export function isBranchPaused(
  branch: Pick<BranchDetail, 'ordersPausedUntil' | 'tradingState' | 'snoozedUntil'> | undefined,
): boolean {
  if (!branch) return false;
  if (branch.ordersPausedUntil) {
    const until = Date.parse(branch.ordersPausedUntil);
    if (!Number.isNaN(until) && until > Date.now()) return true;
  }
  return (
    branch.tradingState === 'PAUSED' || branch.tradingState === 'SNOOZED' || !!branch.snoozedUntil
  );
}

export async function getBusiness() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/business');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBusiness(body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/business', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBusinessBranding(body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PUT('/merchant/v1/business/branding', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listBranches() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/branches');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export type CreateBranchBody = {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region?: string;
  postcode: string;
  phone?: string;
  pickupInstructions?: string;
  providerStoreReference?: string;
  email?: string;
  verticals?: string[];
  fulfilmentModes?: Array<'DELIVERY' | 'COLLECTION'>;
  defaultPrepMinutes?: number;
  acceptsScheduled?: boolean;
  description?: string;
  copySettingsFromBranchId?: string;
  copyCatalogueFromBranchId?: string;
  timezone?: string;
};

export function buildCreateBranchBody(input: CreateBranchBody): Record<string, unknown> {
  const verticals = (input.verticals ?? []).map((v) => v.trim()).filter(Boolean);
  return {
    name: input.name.trim(),
    addressLine1: input.addressLine1.trim(),
    ...(input.addressLine2?.trim() ? { addressLine2: input.addressLine2.trim() } : {}),
    city: input.city.trim(),
    ...(input.region?.trim() ? { region: input.region.trim() } : {}),
    postcode: input.postcode.trim(),
    address: {
      line1: input.addressLine1.trim(),
      ...(input.addressLine2?.trim() ? { line2: input.addressLine2.trim() } : {}),
      city: input.city.trim(),
      ...(input.region?.trim() ? { region: input.region.trim() } : {}),
      postcode: input.postcode.trim(),
    },
    ...(input.phone?.trim() ? { phone: input.phone.trim() } : {}),
    ...(input.pickupInstructions?.trim()
      ? { pickupInstructions: input.pickupInstructions.trim() }
      : {}),
    ...(input.providerStoreReference?.trim()
      ? { providerStoreReference: input.providerStoreReference.trim() }
      : {}),
    ...(input.email?.trim() ? { email: input.email.trim() } : {}),
    ...(verticals.length ? { verticals } : {}),
    ...(input.fulfilmentModes?.length ? { fulfilmentModes: input.fulfilmentModes } : {}),
    ...(input.defaultPrepMinutes != null ? { defaultPrepMinutes: input.defaultPrepMinutes } : {}),
    ...(input.acceptsScheduled != null ? { acceptsScheduled: input.acceptsScheduled } : {}),
    ...(input.description?.trim() ? { description: input.description.trim() } : {}),
    ...(input.copySettingsFromBranchId
      ? { copySettingsFromBranchId: input.copySettingsFromBranchId }
      : {}),
    ...(input.copyCatalogueFromBranchId
      ? { copyCatalogueFromBranchId: input.copyCatalogueFromBranchId }
      : {}),
    ...(input.timezone ? { timezone: input.timezone } : {}),
  };
}

export async function createBranch(body: CreateBranchBody) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/branches', {
    body: buildCreateBranchBody(body) as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getBranch(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/branches/{id}', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBranch(
  branchId: string,
  body: {
    name?: string;
    addressLine1?: string;
    addressLine2?: string | null;
    city?: string;
    region?: string | null;
    postcode?: string;
    phone?: string | null;
    pickupInstructions?: string | null;
    providerStoreReference?: string | null;
    email?: string | null;
    description?: string | null;
    verticals?: string[];
    supportsDelivery?: boolean;
    supportsCollection?: boolean;
    defaultPrepMinutes?: number;
    acceptsScheduled?: boolean;
    fulfilmentModes?: Array<'DELIVERY' | 'COLLECTION'>;
  },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/branches/{id}', {
    params: { path: { id: branchId } },
    body: buildUpdateBranchBody(body) as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export type UpdateBranchBody = Parameters<typeof updateBranch>[1];

export function buildUpdateBranchBody(body: {
  name?: string;
  addressLine1?: string;
  addressLine2?: string | null;
  city?: string;
  region?: string | null;
  postcode?: string;
  phone?: string | null;
  pickupInstructions?: string | null;
  providerStoreReference?: string | null;
  email?: string | null;
  description?: string | null;
  verticals?: string[];
  supportsDelivery?: boolean;
  supportsCollection?: boolean;
  defaultPrepMinutes?: number;
  acceptsScheduled?: boolean;
  fulfilmentModes?: Array<'DELIVERY' | 'COLLECTION'>;
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (body.name != null) payload.name = body.name;
  if (body.phone !== undefined) payload.phone = body.phone;
  if (body.pickupInstructions !== undefined) payload.pickupInstructions = body.pickupInstructions;
  if (body.providerStoreReference !== undefined) {
    payload.providerStoreReference = body.providerStoreReference;
  }
  if (body.email !== undefined) payload.email = body.email;
  if (body.description !== undefined) payload.description = body.description;
  if (body.verticals) payload.verticals = body.verticals;
  if (body.supportsDelivery != null) payload.supportsDelivery = body.supportsDelivery;
  if (body.supportsCollection != null) payload.supportsCollection = body.supportsCollection;
  if (body.defaultPrepMinutes != null) payload.defaultPrepMinutes = body.defaultPrepMinutes;
  if (body.acceptsScheduled != null) payload.acceptsScheduled = body.acceptsScheduled;
  if (body.fulfilmentModes) payload.fulfilmentModes = body.fulfilmentModes;
  if (
    body.addressLine1 ||
    body.city ||
    body.postcode ||
    body.addressLine2 !== undefined ||
    body.region !== undefined
  ) {
    payload.address = {
      ...(body.addressLine1 ? { line1: body.addressLine1 } : {}),
      ...(body.addressLine2 !== undefined ? { line2: body.addressLine2 } : {}),
      ...(body.city ? { city: body.city } : {}),
      ...(body.region !== undefined ? { region: body.region } : {}),
      ...(body.postcode ? { postcode: body.postcode } : {}),
    };
    if (body.addressLine1) payload.addressLine1 = body.addressLine1;
    if (body.addressLine2 !== undefined) payload.addressLine2 = body.addressLine2;
    if (body.city) payload.city = body.city;
    if (body.region !== undefined) payload.region = body.region;
    if (body.postcode) payload.postcode = body.postcode;
  }
  return payload;
}

export type PickupLocationProof = {
  branch: BranchDetail;
  pickupPinProof: string;
  pickupPinProofExpiresAt: string;
};

export function pickupLocationProofFromEnvelope(
  envelope: unknown,
): PickupLocationProof | undefined {
  const data = readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (!data || typeof data.pickupPinProof !== 'string') return undefined;
  const branch = branchFromEnvelope(envelope);
  if (!branch) return undefined;
  return {
    branch,
    pickupPinProof: data.pickupPinProof,
    pickupPinProofExpiresAt:
      typeof data.pickupPinProofExpiresAt === 'string' ? data.pickupPinProofExpiresAt : '',
  };
}

export async function revalidateBranchLocation(branchId: string, providerAddressId?: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/branches/{id}/location/revalidate',
    {
      params: { path: { id: branchId } },
      body: providerAddressId ? { providerAddressId } : {},
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  const proof = pickupLocationProofFromEnvelope(data);
  if (!proof) throw new Error('Pickup location validation returned an incomplete response');
  return proof;
}

export async function confirmBranchPickupPin(
  branchId: string,
  body: { latitude: number; longitude: number; proof: string },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/branches/{id}/location/confirm-pickup-pin',
    {
      params: { path: { id: branchId } },
      body,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getOpeningHours(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/branches/{id}/opening-hours', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export function buildOpeningHoursPutBody(
  draft: HoursDraft | Record<string, HoursDayDraft>,
  fulfilmentMethod = 'DELIVERY',
  lastOrderOffsetMinutes?: number,
): Record<string, unknown> {
  const slots = WEEKDAYS.flatMap((day) => {
    const row = draft[day];
    if (!row || row.closed) return [];
    return periodsFromDayDraft(row).map((period) => ({
      dayOfWeek: WEEKDAY_TO_DOW[day],
      opensAt: period.open || '09:00',
      closesAt: period.close || '22:00',
    }));
  });
  return {
    fulfilmentMethod,
    slots,
    ...(lastOrderOffsetMinutes != null ? { lastOrderOffsetMinutes } : {}),
  };
}

export async function updateOpeningHours(
  branchId: string,
  draft: HoursDraft | Record<string, HoursDayDraft>,
  fulfilmentMethod = 'DELIVERY',
  lastOrderOffsetMinutes?: number,
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PUT('/merchant/v1/branches/{id}/opening-hours', {
    params: { path: { id: branchId } },
    body: buildOpeningHoursPutBody(draft, fulfilmentMethod, lastOrderOffsetMinutes) as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export function openingHoursFulfilmentMethods(
  branch?: {
    supportsDelivery?: boolean;
    supportsCollection?: boolean;
  } | null,
): string[] {
  const methods: string[] = [];
  if (branch?.supportsDelivery !== false) methods.push('DELIVERY');
  if (branch?.supportsCollection === true) methods.push('COLLECTION');
  return methods.length > 0 ? methods : ['DELIVERY'];
}

export async function updateOpeningHoursForMethods(
  branchId: string,
  draft: HoursDraft | Record<string, HoursDayDraft>,
  methods: string[],
  lastOrderOffsetMinutes?: number,
) {
  const unique = [...new Set(methods.map((method) => method.toUpperCase()).filter(Boolean))];
  const target = unique.length > 0 ? unique : ['DELIVERY'];
  for (let i = 0; i < target.length; i += 1) {
    await updateOpeningHours(
      branchId,
      draft,
      target[i],
      i === 0 ? lastOrderOffsetMinutes : undefined,
    );
  }
}

export async function copyOpeningHoursToBranches(
  sourceDraft: HoursDraft | Record<string, HoursDayDraft>,
  targetBranchIds: string[],
  fulfilmentMethod: string | string[] = 'DELIVERY',
  lastOrderOffsetMinutes?: number,
) {
  const methods = Array.isArray(fulfilmentMethod) ? fulfilmentMethod : [fulfilmentMethod];
  const results: Array<{ branchId: string; ok: boolean; error?: string }> = [];
  for (const id of targetBranchIds) {
    try {
      await updateOpeningHoursForMethods(id, sourceDraft, methods, lastOrderOffsetMinutes);
      results.push({ branchId: id, ok: true });
    } catch (err) {
      results.push({ branchId: id, ok: false, error: (err as Error).message });
    }
  }
  return results;
}

export async function getSpecialClosures(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/branches/{id}/special-hours', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function addSpecialClosure(
  branchId: string,
  body: { date: string; isClosed?: boolean; reason?: string; note?: string },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/branches/{id}/special-hours', {
    params: { path: { id: branchId } },
    body: {
      date: body.date,
      isClosed: body.isClosed ?? true,
      reason: body.reason ?? body.note,
      note: body.note ?? body.reason,
      fulfilmentMethod: 'DELIVERY',
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function removeSpecialClosure(branchId: string, overrideId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.DELETE(
    '/merchant/v1/branches/{id}/special-hours/{overrideId}',
    {
      params: { path: { id: branchId, overrideId } },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getServiceArea(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/branches/{id}/service-area', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateServiceArea(branchId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PUT('/merchant/v1/branches/{id}/service-area', {
    params: { path: { id: branchId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function previewServiceArea(branchId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/branches/{id}/service-area/preview',
    {
      params: { path: { id: branchId } },
      body: body as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getAcceptanceSettings(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/branches/{id}/acceptance', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBranchCapacity(branchId: string, body: CapacitySettings) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/branches/{id}/capacity', {
    params: { path: { id: branchId } },
    body: {
      maxConcurrentOrders: body.maxConcurrentOrders,
      maxOrdersPerSlot: body.maxOrdersPerSlot,
      throttleMinutes: body.throttleMinutes,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function pauseBranchTrading(
  branchId: string,
  body: { reason: string; minutes: number },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/branches/{id}/snooze', {
    params: { path: { id: branchId } },
    body: { minutes: body.minutes, reason: body.reason } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function resumeBranchTrading(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.DELETE('/merchant/v1/branches/{id}/snooze', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function goLiveBranch(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/branches/{id}/go-live', {
    params: { path: { id: branchId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export type SubstitutionPolicy = 'ALWAYS_ASK' | 'BEST_MATCH' | 'REFUND';

export function substitutionPolicyFromEnvelope(envelope: unknown): SubstitutionPolicy {
  const data = readDataFromEnvelope<Record<string, unknown>>(envelope) ?? {};
  const raw =
    (data.substitutionPolicy as string | undefined) ??
    (data.policy as string | undefined) ??
    (readObjectFieldFromEnvelope<{ substitutionPolicy?: string }>(envelope, ['substitutionPolicy'])
      ?.substitutionPolicy as string | undefined);
  if (raw === 'BEST_MATCH' || raw === 'REFUND' || raw === 'ALWAYS_ASK') return raw;
  return 'ALWAYS_ASK';
}

export async function getSubstitutionPolicy(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET(
    '/merchant/v1/branches/{id}/substitution-policy' as never,
    {
      params: { path: { id: branchId } },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function setSubstitutionPolicy(branchId: string, policy: SubstitutionPolicy) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PUT(
    '/merchant/v1/branches/{id}/substitution-policy',
    {
      params: { path: { id: branchId } },
      body: { policy } as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
